import React, { useState, useCallback, useMemo, useRef } from "react";
import { View, StyleSheet, ScrollView, Platform } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { AttachmentProps, AttachmentFile, ValidationError } from "./types";
import { FilePreview } from "./components/FilePreview";
import { validateFiles, fileToAttachmentFile } from "./lib/fileUtils";

// Simplified DropZone without heavy event listeners
function SimpleDropZone({
  onFilesDropped,
  onError,
  children,
  testID,
  disabled = false,
}: {
  onFilesDropped: (files: File[]) => void;
  onError?: (error: string) => void;
  children: React.ReactNode;
  testID?: string;
  disabled?: boolean;
}) {
  const { colors } = useTheme();
  const [isDragging, setIsDragging] = useState(false);

  // Simplified web-only drag handlers without complex event management
  const handleDragOver = (e: any) => {
    if (Platform.OS !== "web" || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: any) => {
    if (Platform.OS !== "web" || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: any) => {
    if (Platform.OS !== "web" || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer?.files || []) as File[];
    if (files.length > 0) {
      onFilesDropped(files);
    } else {
      onError?.("No valid files found");
    }
  };

  return (
    <View
      testID={testID}
      style={[
        styles.dropZone,
        {
          borderColor: isDragging ? colors.primary : colors.border,
          backgroundColor: isDragging ? colors.primary + "10" : "transparent",
        },
        disabled && styles.disabled,
      ]}
      {...(Platform.OS === "web" && {
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
      })}
    >
      {children}
    </View>
  );
}

// Simplified file picker without heavy mobile imports
const openSimpleFilePicker = (options: {
  acceptedTypes?: string[];
  multiple?: boolean;
  onFiles: (files: File[]) => void;
}) => {
  if (Platform.OS !== "web") return;

  const input = document.createElement("input");
  input.type = "file";
  input.multiple = options.multiple ?? true;
  input.style.display = "none";

  if (options.acceptedTypes && options.acceptedTypes.length > 0) {
    input.accept = options.acceptedTypes.join(",");
  }

  input.addEventListener("change", (e) => {
    const target = e.target as HTMLInputElement;
    if (target.files) {
      const files = Array.from(target.files);
      options.onFiles(files);
    }
    input.remove();
  });

  document.body.appendChild(input);
  input.click();
};

export function FixedAttachment({
  files = [],
  onFilesChange,
  autoUpload = true,
  maxFiles = 10,
  maxFileSize = 50 * 1024 * 1024, // 50MB default
  acceptedTypes,
  mode = "grid",
  showPreviews = true,
  dragDropEnabled = true,
  showUploadButton = true,
  placeholder = "Drop files here or click to select",
  style,
  testID = "fixed-attachment-component",
  onError,
  onFileRemove,
  onFilePreview,
}: AttachmentProps) {
  const { colors } = useTheme();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  // Use refs for stable references to prevent useCallback recreation
  const onFilesChangeRef = useRef(onFilesChange);
  const onErrorRef = useRef(onError);
  const onFileRemoveRef = useRef(onFileRemove);

  // Update refs when props change
  onFilesChangeRef.current = onFilesChange;
  onErrorRef.current = onError;
  onFileRemoveRef.current = onFileRemove;

  const canAddFiles = useMemo(() => {
    return maxFiles === undefined || files.length < maxFiles;
  }, [files.length, maxFiles]);

  // Stable error handler
  const handleError = useCallback((error: string) => {
    onErrorRef.current?.(error);
    console.warn("Fixed Attachment Error:", error);
  }, []);

  // Stable file validation handler
  const handleFileValidation = useCallback(
    (fileList: File[]): AttachmentFile[] => {
      const validationErrors = validateFiles(fileList, {
        maxFiles,
        maxFileSize,
        acceptedTypes,
        existingFiles: files,
      });

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        validationErrors.forEach((error) => handleError(error.message));
        return [];
      }

      return fileList.map(fileToAttachmentFile);
    },
    [maxFiles, maxFileSize, acceptedTypes, files, handleError],
  );

  // Stable files dropped handler
  const handleFilesDropped = useCallback(
    (fileList: File[]) => {
      if (!canAddFiles) {
        handleError("Maximum number of files reached");
        return;
      }

      const validatedFiles = handleFileValidation(fileList);
      if (validatedFiles.length > 0) {
        const updatedFiles = [...files, ...validatedFiles];
        onFilesChangeRef.current?.(updatedFiles);
        setErrors([]);
      }
    },
    [canAddFiles, handleFileValidation, files, handleError],
  );

  // Stable file picker handler
  const handleFilePickerOpen = useCallback(() => {
    if (!canAddFiles) {
      handleError("Maximum number of files reached");
      return;
    }

    openSimpleFilePicker({
      acceptedTypes,
      multiple: maxFiles !== 1,
      onFiles: (fileList) => {
        const validatedFiles = handleFileValidation(fileList);
        if (validatedFiles.length > 0) {
          const updatedFiles = [...files, ...validatedFiles];
          onFilesChangeRef.current?.(updatedFiles);
          setErrors([]);
        }
      },
    });
  }, [canAddFiles, acceptedTypes, maxFiles, handleFileValidation, files, handleError]);

  // Stable file remove handler
  const handleFileRemove = useCallback(
    (fileId: string) => {
      const updatedFiles = files.filter((file) => file.id !== fileId);
      onFilesChangeRef.current?.(updatedFiles);
      onFileRemoveRef.current?.(fileId);

      if (errors.length > 0) {
        setErrors([]);
      }
    },
    [files, errors.length],
  );

  const renderUploadArea = () => {
    const isEmpty = files.length === 0;
    const supportsDragDrop = Platform.OS === "web" && dragDropEnabled;

    return (
      <SimpleDropZone
        onFilesDropped={handleFilesDropped}
        onError={handleError}
        disabled={!canAddFiles}
        testID={`${testID}-dropzone`}
      >
        <View
          style={[
            styles.uploadArea,
            { borderColor: colors.border },
            isEmpty && styles.emptyUploadArea,
            !canAddFiles && styles.disabledUploadArea,
          ]}
        >
          {isEmpty && (
            <View style={styles.placeholderContent}>
              <MaterialCommunityIcons name="cloud-upload" size={48} color={colors.placeholder} />
              <ThemedText style={[styles.placeholderText, { color: colors.placeholder }]}>{placeholder}</ThemedText>

              {supportsDragDrop && (
                <ThemedText style={[styles.dragHint, { color: colors.textSecondary }]}>
                  Drag & drop supported
                </ThemedText>
              )}

              {acceptedTypes && acceptedTypes.length > 0 && (
                <ThemedText style={[styles.acceptedTypes, { color: colors.textSecondary }]}>
                  Accepted: {acceptedTypes.join(", ")}
                </ThemedText>
              )}
            </View>
          )}

          {showUploadButton && canAddFiles && (
            <View style={styles.buttonContainer}>
              <Button
                iconPosition="right"
                text={isEmpty ? "Choose Files" : "Add More Files"}
                icon="plus"
                onPress={handleFilePickerOpen}
                testID={`${testID}-upload-button`}
                style={{ gap: 8, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 }}
              />
            </View>
          )}
        </View>
      </SimpleDropZone>
    );
  };

  const renderFilesList = () => {
    if (files.length === 0) return null;

    if (mode === "compact") {
      return (
        <View style={styles.compactList}>
          {files.map((file, index) => (
            <FilePreview
              key={file.id}
              file={file}
              mode="compact"
              onRemove={handleFileRemove}
              onPreview={onFilePreview}
              testID={`${testID}-file-${index}`}
            />
          ))}
        </View>
      );
    }

    if (mode === "list") {
      return (
        <ScrollView style={styles.filesList} showsVerticalScrollIndicator={false}>
          {files.map((file, index) => (
            <FilePreview
              key={file.id}
              file={file}
              mode="list"
              onRemove={handleFileRemove}
              onPreview={onFilePreview}
              testID={`${testID}-file-${index}`}
            />
          ))}
        </ScrollView>
      );
    }

    // Grid mode
    return (
      <ScrollView
        style={styles.filesGrid}
        contentContainerStyle={styles.gridContent}
        showsVerticalScrollIndicator={false}
      >
        {files.map((file, index) => (
          <FilePreview
            key={file.id}
            file={file}
            mode="grid"
            onRemove={handleFileRemove}
            onPreview={onFilePreview}
            showProgress={true}
            testID={`${testID}-file-${index}`}
          />
        ))}
      </ScrollView>
    );
  };

  return (
    <View style={[styles.container, style]} testID={testID}>
      {/* Error Display */}
      {errors.length > 0 && (
        <View
          style={[
            styles.errorsContainer,
            { backgroundColor: colors.dangerText + "10", borderColor: colors.dangerText },
          ]}
        >
          {errors.map((error, index) => (
            <ThemedText key={index} style={[styles.errorText, { color: colors.dangerText }]}>
              {error.message}
            </ThemedText>
          ))}
        </View>
      )}

      {/* Upload Area */}
      {renderUploadArea()}

      {/* Files List */}
      {renderFilesList()}

      {/* Summary */}
      {files.length > 0 && (
        <View style={styles.summary}>
          <ThemedText style={[styles.summaryText, { color: colors.textSecondary }]}>
            {files.length} of {maxFiles} files selected
          </ThemedText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  dropZone: {
    borderWidth: 2,
    borderStyle: "dashed",
    borderRadius: 8,
    position: "relative",
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  uploadArea: {
    padding: 24,
    borderRadius: 8,
    alignItems: "center",
    gap: 16,
  },
  emptyUploadArea: {
    minHeight: 120,
    justifyContent: "center",
  },
  disabledUploadArea: {
    opacity: 0.6,
  },
  placeholderContent: {
    alignItems: "center",
    gap: 8,
  },
  placeholderText: {
    fontSize: 16,
    fontWeight: "500",
    textAlign: "center",
  },
  dragHint: {
    fontSize: 12,
    textAlign: "center",
  },
  acceptedTypes: {
    fontSize: 11,
    textAlign: "center",
    fontStyle: "italic",
  },
  buttonContainer: {
    marginTop: 8,
  },
  errorsContainer: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 4,
  },
  errorText: {
    fontSize: 12,
  },
  compactList: {
    gap: 4,
  },
  filesList: {
    maxHeight: 300,
  },
  filesGrid: {
    maxHeight: 400,
  },
  gridContent: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  summary: {
    alignItems: "center",
    paddingTop: 8,
  },
  summaryText: {
    fontSize: 12,
  },
});
